// react
import { useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { Link, useNavigate } from '@tanstack/react-router';

// material ui icons
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import LightRoundedIcon from '@mui/icons-material/LightRounded';
import RouterRoundedIcon from '@mui/icons-material/RouterRounded';

// material ui
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';

// shared components
import DataGridNoRowsCustomOverlay from '@/components/ui/datagrid/DataGridNoRowsOverlay';
import { DataGridToolbar } from '@/components/ui/datagrid/DataGridToolbar';
import RowActions from '@/components/ui/datagrid/RowActions';

// components
import { useSessionAuth } from '@/contexts/SessionAuthContext';
import { useDatagridLocaleText } from '@/hooks/useDatagridLocaleText';
import { useLocalStorageJson } from '@/hooks/useLocalStorageJson';
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import { deriveSensorStatus } from '../utils/deriveSensorStatus';
import { DataIntervalStatus } from './DataIntervalStatus';
import DeleteSensorDialog from './DeleteSensorDialog';
import SensorStatusCell from './SensorStatusCell';

// Helper function to process sensor data
const processSensorData = (sensor) => {
  const partialRow = {
    id: sensor.SensorId,
    SensorId: sensor.SensorId,
    DataLastRecorded: sensor.DataLastRecorded ?? null,
    LatestSensorActivities: sensor.LatestSensorActivities ?? [],
    PairedGatewayId: sensor.PairedGatewayId,
    SensorType: sensor.SensorType,
    DataInterval: sensor.DataInterval,
    TargetDataInterval: sensor.TargetDataInterval,
    CurrentApplicationFirmware: sensor.CurrentApplicationFirmware,
    CalibrationType: sensor.CalibrationType,
    VolumeConstant: sensor.VolumeConstant,
    FlowPipeDiameter: sensor.FlowPipeDiameter,
    UnitSensors: sensor.UnitSensors ?? [],
  };

  return { ...partialRow, Online: deriveSensorStatus(sensor) };
};

const SensorDataGrid = (props) => {
  const { sensors, isLoading } = props;
  const { t } = useTranslation('units-gateways-sensors');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAdmin } = useSessionAuth();

  const [rows, setRows] = useState(() => sensors?.map(processSensorData) ?? []);
  const [lastSensors, setLastSensors] = useState(sensors);
  const [selectedRow, setSelectedRow] = useState(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sensorToDelete, setSensorToDelete] = useState(null);

  const handleDeleteSensor = (sensor) => {
    setSensorToDelete(sensor);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSensorToDelete(null);
  };

  // Update rows when sensors data changes
  if (sensors !== lastSensors) {
    const newRows = sensors?.map(processSensorData) ?? [];
    setRows(newRows);
    setLastSensors(sensors);
  }

  const handleViewSensor = (params) => {
    if (selectedRow && selectedRow.id === params.row.id) {
      setSelectedRow(null);
    } else {
      setSelectedRow(params.row);
      navigate({ to: `${params.id}` });
    }
  };

  const getRowClassName = (params) => {
    return selectedRow && selectedRow.id === params.id ? 'selected-row' : '';
  };

  const [dataGridState, setDataGridState] = useLocalStorageJson(
    'Sensors-DataGrid-Presets',
    {
      presets: [
        {
          name: 'Default',
          visibilityList: {
            DataLastRecorded: false,
            DataInterval: false,
            CurrentApplicationFirmware: false,
          },
          density: 'standard',
          sortModel: [],
        },
      ],
      chosenPreset: 0,
    },
  );

  const handleVisibilityChange = (model) => {
    setDataGridState((prev) => ({
      ...prev,
      presets: prev.presets.map((p, i) =>
        i === prev.chosenPreset ? { ...p, visibilityList: model } : p,
      ),
    }));
  };

  const handleDensityChange = (density) => {
    setDataGridState((prev) => ({
      ...prev,
      presets: prev.presets.map((p, i) =>
        i === prev.chosenPreset ? { ...p, density } : p,
      ),
    }));
  };

  const handleSortModelChange = (model) => {
    setDataGridState((prev) => ({
      ...prev,
      presets: prev.presets.map((p, i) =>
        i === prev.chosenPreset ? { ...p, sortModel: model } : p,
      ),
    }));
  };

  const handleCopySensorId = async (sensor) => {
    if (!sensor?.SensorId) return;
    try {
      await navigator.clipboard.writeText(sensor.SensorId);
      dispatch(
        showSnackbar({
          message:
            t?.('sensors.sensorIdCopied', { id: sensor.SensorId }) ||
            `Sensor ID copied: ${sensor.SensorId}`,
          severity: 'success',
        }),
      );
    } catch (err) {
      console.error('Failed to copy sensor ID:', err);
      dispatch(
        showSnackbar({
          message:
            t?.('sensors.sensorIdCopyFailed') || 'Failed to copy Sensor ID',
          severity: 'error',
        }),
      );
    }
  };

  const adminColumns = [
    {
      field: 'DataInterval',
      headerName: t('sensors.dataInterval'),
      flex: 1,
      renderCell: (params) => (
        <Box display="flex" sx={{ height: '100%' }}>
          <DataIntervalStatus
            sensorId={params.row.SensorId}
            sensor={params.row}
            variant="body2"
            size="small"
            editable={true}
          />
        </Box>
      ),
    },
    {
      field: 'CurrentApplicationFirmware',
      headerName: t('sensors.firmware'),
      renderCell: (params) => (
        <Box display="flex" alignItems="center" sx={{ height: '100%' }}>
          <Typography variant="body2">
            {params.value || t('common.notAvailable')}
          </Typography>
        </Box>
      ),
    },
  ];

  const columns = [
    {
      field: 'Online',
      headerName: t('sensors.status'),
      width: 120,
      valueOptions: ['Online', 'Degraded', 'Offline'],
      filterable: true,
      renderCell: (params) => <SensorStatusCell sensor={params.row} />,
    },
    {
      field: 'id',
      headerName: t('sensors.sensorId', { defaultValue: 'Sensor ID' }),
      flex: 0.5,
      renderCell: (params) => (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            height: '100%',
            width: '100%',
            '& .copy-button': {
              opacity: 0,
              transition: 'opacity 0.2s ease',
            },
            '&:hover .copy-button': {
              opacity: 1,
            },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params?.row?.SensorId || t('common.notAvailable')}
          </Typography>
          <IconButton
            className="copy-button"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleCopySensorId(params.row);
            }}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            <ContentCopyRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
    {
      field: 'SensorType',
      headerName: t('sensors.type'),
      flex: 1,
      renderCell: (params) => {
        return (
          <Stack direction="row" alignItems="center" spacing={1} height="100%">
            <Typography variant="body2">
              {params.value}{' '}
              {t('sensors.calibrationTypeInline', {
                value: params.row.CalibrationType,
              })}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'PairedGatewayId',
      headerName: t('sensors.pairedGateway'),
      flex: 0.75,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={1} height="100%">
          <RouterRoundedIcon fontSize="small" color="action" />
          <Typography
            variant="body2"
            component={Link}
            to={`/$accountId/gateways/${params.value}`}
            color="primary"
            sx={{
              ':hover': { textDecoration: 'underline' },
            }}
          >
            {params.value || '-'}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'DataLastRecorded',
      headerName: t('sensors.lastRecorded'),
      flex: 1,
      type: 'dateTime',
      valueGetter: (value) => (value ? new Date(value) : null),
      valueFormatter: (value) => {
        if (!value) return '-';
        return value.toLocaleString('da-DK', {
          timeZone: 'Europe/Copenhagen',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
      renderCell: (params) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="body2">
            {params.value
              ? params.value.toLocaleString('da-DK', {
                  timeZone: 'Europe/Copenhagen',
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '-'}
          </Typography>
        </Box>
      ),
    },
    ...(isAdmin ? adminColumns : []),
    {
      field: 'UnitSensors',
      headerName: t('sensors.meteredUnits'),
      flex: 2,
      sortable: false,
      valueGetter: (value) => {
        const unitSensors = value || [];
        // Get unique units by UnitId
        const uniqueUnits = Array.from(
          new Map(
            unitSensors
              .filter((us) => us.Unit?.UnitId)
              .map((us) => [us.Unit.UnitId, us.Unit.Name]),
          ).values(),
        );
        return uniqueUnits.filter(Boolean).join(', ');
      },
      renderCell: (params) => {
        const unitSensors = params.row.UnitSensors || [];
        if (unitSensors.length === 0) {
          return (
            <Box display="flex" alignItems="center" height="100%">
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            </Box>
          );
        }

        // Deduplicate by UnitId
        const uniqueUnitSensors = Array.from(
          new Map(
            unitSensors
              .filter((us) => us.Unit?.UnitId)
              .map((us) => [us.Unit.UnitId, us]),
          ).values(),
        );

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                alignItems: 'flex-start',
                alignContent: 'flex-start',
                width: '100%',
                py: 0.5,
              }}
            >
              {uniqueUnitSensors.map((unitSensor) => (
                <Chip
                  key={unitSensor.Unit.UnitId}
                  component={Link}
                  to={`/$accountId/units/${unitSensor.Unit.UnitId}`}
                  icon={<LightRoundedIcon sx={{ color: 'white !important' }} />}
                  label={`${unitSensor.Unit?.Name || t('common.notAvailable')} (${unitSensor.Unit.UnitId})`}
                  color="info"
                  size="small"
                  clickable
                  sx={{
                    borderRadius: 1,
                    fontSize: '0.75rem',

                    color: 'white',
                    '& .MuiChip-label': {
                      color: 'white',
                    },
                    '&:hover': {
                      opacity: 0.9,
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: t('sensors.actions'),
      width: 120,
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <RowActions
            row={params.row}
            onView={() => handleViewSensor(params.row)}
            onDelete={() => handleDeleteSensor(params.row)}
          />
        </Box>
      ),
    },
  ];

  const localeText = useDatagridLocaleText();

  return (
    <>
      <Box data-tour="sensor-overview-grid">
        <DataGridPremium
          showToolbar
          rows={rows}
          columns={columns}
          getRowClassName={getRowClassName}
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => 84}
          noRowsOverlay={DataGridNoRowsCustomOverlay}
          isLoading={isLoading}
          rowsName="Sensors"
          localeText={localeText}
          pagination
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: 50 } },
          }}
          pageSizeOptions={[50, 100]}
          density={dataGridState.presets[dataGridState.chosenPreset].density}
          onDensityChange={handleDensityChange}
          onColumnVisibilityModelChange={handleVisibilityChange}
          columnVisibilityModel={{
            ...dataGridState.presets[dataGridState.chosenPreset].visibilityList,
          }}
          sortModel={
            dataGridState.presets[dataGridState.chosenPreset].sortModel ?? []
          }
          onSortModelChange={handleSortModelChange}
          slots={{
            toolbar: DataGridToolbar,
          }}
          slotProps={{
            toolbar: {
              showFilters: true,
              showColumns: true,
              showDivider: false,
            },
          }}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-row': {
              minHeight: '52px !important',
            },
            '& .MuiDataGrid-cell': {
              minHeight: '52px !important',
            },
            '& .MuiDataGrid-cell[data-field="UnitSensors"]': {
              alignItems: 'flex-start',
            },
          }}
        />
      </Box>

      <DeleteSensorDialog
        open={deleteDialogOpen}
        sensor={sensorToDelete}
        onClose={handleDeleteCancel}
        onDeleted={(deleted) =>
          setRows((prev) => prev.filter((r) => r.id !== deleted.SensorId))
        }
      />
    </>
  );
};

export default SensorDataGrid;
